<script lang="ts" setup>
// @ts-expect-error JS library
import Player from '@vimeo/player'
import { storeToRefs } from 'pinia'
import { useRoot } from '~/store'
import { Iconify } from '~/types/assets'
import { useRadar } from '~/store/radar'
import { useLocaleThemePath } from '~/composables/locale'
import { capitalize } from '~/utils'

const page = '_media'
const $radar = useRadar()
const { routeRaw } = storeToRefs($radar)
const { setWheelsCount } = useRoot()
const { t } = useI18n()
const i18nHead = useLocaleHead()

const player = ref(null)
const playerEl = ref(null)
const mediaSlug = routeRaw.value?.params?.media

const { data: media, error } = await useAsyncData(async () => await $fetch(`/api/media/${mediaSlug}`))

if (!media.value || error.value) {
  const event = useRequestEvent()
  console.error(error.value)
  if (event) setResponseStatus(event, 404, 'Media not found.')
}

$radar.setPreviousRoutePath('/media')

useSeoMeta({
  title: () => t(`routes.${page}.title`, { name: capitalize(media.value?.name) }),
  ogTitle: () => t(`routes.${page}.title`, { name: capitalize(media.value?.name) }),
  description: () =>
    t(`routes.${page}.description`, {
      name: capitalize(media.value?.name),
      description: media?.value?.description
    }),
  ogDescription: () =>
    t(`routes.${page}.description`, {
      name: capitalize(media.value?.name),
      description: media?.value?.description
    })
})

useHead(i18nHead.value)

useStructuredData(page)

if (media.value) useSDVideo(media.value)

const vimeoId = computed((): number | null => {
  const chunks = media.value?.uri?.split('/')
  return chunks && Array.isArray(chunks) ? Number.parseInt(chunks?.[chunks.length - 1]) : null
})

setWheelsCount(media.value?.skills && Array.isArray(media?.value?.skills) && media.value?.skills.length > 0 ? 2 : 1)

onMounted(() => {
  try {
    if (vimeoId.value) {
      player.value = new Player(playerEl.value, {
        id: vimeoId.value,
        width: 800
      })
    }
  } catch (err) {
    console.error(err)
  }
})
</script>

<template lang="pug">
.media__details.container-fluid
  .row
    .col-12
      .media__details__hero(v-if="media")
        h1 {{  media?.name ? media?.name?.toLowerCase()?.replace('.mov', '') : '' }}
        .media__details__player
          .media-player(ref="playerEl")
      .media__404(v-else)
        h1 Error 404 - Media Not Found.
        p The server failed to retrieve the media:
          span {{ mediaSlug }}.
        UIGridImage(
          src="https://s3.rottigni.tech/portfolio.rottigni.tech/static_content/404/hero-404-5.jpg"
          :height="4"
        )
        NuxtLink(:to="useLocaleThemePath(`/`)") Home
  .row(
    v-if="media?.skills && Array.isArray(media?.skills) && media?.skills.length > 0"
  )
    .col-12
      .media__details__related
        h2 {{ $t('_media.skills') }}
        ul.media__details__skills
          li(
            v-for="({ skill }, index) in media?.skills"
            :key="`repo-skill-${index}`"
          )
            CardSkill(:skill="skill", :size="Size?.sm")
        h3 {{ $t('_media.topics') }}
        ul.media__details__topics
          li(
            v-for="({ topic }, index) in media?.topics"
            :key="`media-${index}`"
            :style="{ backgroundColor: topic.color }"
          ) {{ `#${topic?.name}` }}

        NuxtLink(
          :to="useLocaleThemePath(`/git/${media?.repo?.name}`)"
          :title="$t('_media.anchors.repo.title', { name: media?.name })"
          :aria-label="$t('_media.anchors.repo.aria-label', { name: media?.name })"
        )
          Icon(:name="Iconify?.github")
          | {{ $t('_media.anchors.repo.label') }}
</template>

<style lang="sass" scoped>
.media__details
  @apply w-full h-full flex-col flex-center relative p-0 py-0
  .row
    @apply w-full h-[100dvh] flex-center m-0
    .media__details__hero,
    .media__details__related
      @apply w-full h-full flex-center flex-col
      h1
        @apply text-3xl text-primary uppercase mb-6 font-bold text-center
      p
        @apply text-secondary
  h2, h3
    @apply text-primary text-2xl font-bold
  a
    @apply text-secondary text-2xl underline flex items-center gap-2
    span.iconify
      @apply bg-secondary text-4xl
  ul.media__details__topics
    @apply flex items-center mb-6 gap-2 mt-6
    li
      @apply py-1 px-2 rounded-[30px] font-bold text-sm underline
  .media__details__player
    @apply w-full h-full flex-center flex-col overflow-hidden mx-auto
    .media-player
      @apply w-full max-w-full mx-auto overflow-hidden flex pb-4 border-4 border-primary bg-dark-800
    :deep(.media-player)
      iframe
        @apply w-full h-[30vh] sm:h-[50vh] bg-dark-800 p-0 m-0 shadow-2xl
  ul.media__details__skills
    @apply flex justify-center gap-4 mt-6 pb-6
.media__404
  h1
    @apply text-primary text-center text-2xl lg:text-4xl pb-6
  p
    @apply w-full text-lg lg:text-2xl text-white text-center max-w-[600px] mx-auto
    span
      @apply text-secondary mx-1
  a
    @apply block w-full text-center text-primary underline text-2xl
</style>
